import { useState } from 'react';

type Options<T> = {
  onChange: (value: T) => void;
  onChangeEnd?: (value: T) => void;
  // Optional normalization applied before draft/commit (e.g. avoiding a zero-width range slider).
  sanitize?: (value: T) => T;
  // How to detect an external value change to re-sync the draft (defaults to reference/`===` equality).
  areEqual?: (a: T, b: T) => boolean;
};

/**
 * Keeps a controlled value in local draft state while the user is interacting (e.g. dragging a slider),
 * committing to the (typically Redux-dispatching) handler only on release — so an interaction doesn't fire
 * a search per tick. Commits on `onChangeEnd` if present, otherwise on `onChange`; a config that supplies
 * both treats `onChange` as a live handler. Read `draft` to display the live value.
 */
export default function useDraftValue<T>(value: T, { onChange, onChangeEnd, sanitize, areEqual = (a, b) => a === b }: Options<T>) {
  const [draft, setDraft] = useState(value);
  const [committed, setCommitted] = useState(value);

  // Re-sync the draft when the committed value changes from outside (e.g. Clear or a unit toggle).
  if (!areEqual(committed, value)) {
    setCommitted(value);
    setDraft(value);
  }

  const normalize = (next: T) => (sanitize ? sanitize(next) : next);
  const commitOnEnd = onChangeEnd ?? onChange;
  const liveOnChange = onChangeEnd ? onChange : undefined;

  return {
    draft,
    onChange: (next: T) => {
      const sanitized = normalize(next);
      setDraft(sanitized);
      liveOnChange?.(sanitized);
    },
    onChangeEnd: (next: T) => commitOnEnd(normalize(next))
  };
}
